import {
  StyledCard,
  StyledInput
} from '@bo-components/common/StyledComponents';
import type { AllowedStatesData } from '@library/types/allowedStates';
import { DEFAULT_ALLOWED_STATES_DATA } from '@bo-constants/allowedStates';
import { FormActionButtons } from '@library/components/buttons';
import StyledModal from '@bo-components/common/StyledModal';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import useUtilDetailsModal from '@bo-hooks/utilsDashboards/useUtilDetailsModal';

export interface AllowedStateDetailsModalProps {
  isNewItem: boolean;
  isOpen: boolean;
  modalData: AllowedStatesData;
  onClose: () => void;
  onSubmit: (formData: Partial<AllowedStatesData>) => void;
  submitMessage: string;
}

export const AllowedStateDetailsModal = ({
  isNewItem,
  isOpen,
  modalData,
  onClose,
  onSubmit,
  submitMessage
}: AllowedStateDetailsModalProps) => {
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<AllowedStatesData>({
    mode: 'onChange'
  });

  const {
    formData,
    setFormData,
    isReadOnly,
    setIsReadOnly,
    onClickEdit,
    handleCancel
  } = useUtilDetailsModal({
    isNewItem,
    defaultItemData: DEFAULT_ALLOWED_STATES_DATA,
    modalData,
    onClose,
    reset
  });

  useEffect(() => {
    if (isNewItem) {
      setIsReadOnly(false);
    }
  }, [isNewItem, setIsReadOnly]);

  useEffect(() => {
    if (modalData) {
      setFormData(modalData);
    }
  }, [modalData, setFormData]);

  useEffect(() => {
    reset(formData);
  }, [reset, formData]);

  const isEditMode = !isReadOnly || isNewItem;

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={onClose}
      onEdit={isNewItem ? undefined : onClickEdit}
      testId="allowed-states-details-modal"
      bigTitle={formData.name}
      smallTitle={isEditMode ? 'Edit Allowed States' : 'View Allowed States'}
    >
      <form>
        <div className="flex gap-4">
          {/* <StyledCard title="General Information">
            <StyledInput
              error={errors.name?.message}
              label="Name"
              name="name"
              readOnly={isReadOnly}
              register={register}
              required={isEditMode}
            />
            <StyledInput
              label="Short Name"
              name="shortName"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Rank"
              name="rank"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Leagues"
              name="leagueNames"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Early Setter Buffer"
              name="earlySettleBuffer"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Post Game Finalization Buffer"
              name="postGameFinalizationBuffer"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="LFG Ignored Leagues"
              name="lfgIgnoredLeagues"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Created At"
              name="createdAt"
              readOnly
              register={register}
            />
            <StyledInput
              label="Updated At"
              name="updatedAt"
              readOnly
              register={register}
            />
          </StyledCard> */}
          <StyledCard title="General Information">
            <StyledInput
              error={errors.name?.message}
              label="Name"
              name="name"
              readOnly={isReadOnly}
              register={register}
              required={isEditMode}
            />
            <StyledInput
              label="State Code"
              name="state_code"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="30-Day Limit (cents)"
              name="thirty_one_day_limit_cents"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="F2P Only"
              name="f2p_only"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Allow Payments"
              name="allow_payments"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Allow Withdrawals"
              name="allow_withdrawals"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Allow Entries"
              name="allow_entries"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Allow Mastercard"
              name="allow_mastercard"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Bypass Playthrough"
              name="bypass_playthrough"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Default Game Mode"
              name="default_game_mode"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Allow Venmo"
              name="allow_venmo"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Allow PayPal"
              name="allow_paypal"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Allow Apple Pay"
              name="allow_applepay"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Allow Bank"
              name="allow_bank"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Allow Card"
              name="allow_card"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Allow Visa"
              name="allow_visa"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Allow Discover"
              name="allow_discover"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Minimum Withdrawal (cents)"
              name="minimum_withdrawal_cents"
              readOnly={isReadOnly}
              register={register}
            />
            <StyledInput
              label="Created At"
              name="created_at"
              readOnly
              register={register}
            />
            <StyledInput
              label="Updated At"
              name="updated_at"
              readOnly
              register={register}
            />
          </StyledCard>
        </div>
      </form>

      {isEditMode && (
        <div className="border-soFresh-140 bg-soFresh-100 sticky bottom-0 flex items-center justify-end gap-4 border-t border-solid py-3 pr-5">
          {submitMessage ? (
            <p>{submitMessage}</p>
          ) : (
            <FormActionButtons
              className="flex items-center justify-end gap-4 border-none py-3 pr-5"
              primary={{
                onClick: handleSubmit(onSubmit),
                disabled: !isValid
              }}
              secondary={{ onClick: handleCancel }}
            />
          )}
        </div>
      )}
    </StyledModal>
  );
};
